import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Phone, MapPin, Mail, Globe } from 'lucide-react';
import AuctioneerPhoto from '../AuctioneerPhoto';
import AuctioneerExpertise from '../AuctioneerExpertise';
import {
    getAuctioneerName,
    getAuctioneerLocation,
    getDesignations,
} from '../../utils/auctioneer';
import {
    AUCTION_PROP_TYPE,
    AUCTIONEER_PROP_TYPE
} from '../../utils/props';
import AuctionPreview from '../AuctionPreview/AuctionPreview';
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator';
import { prefixIfNeeded } from '../../utils/urls';
import { trim } from '../../utils/strings';

import './AuctioneerPage.css';

const AuctioneerPage = ({
    onInitialize,
    auctioneerId,
    auctioneer,
    auctioneerLoading,
    hideHeader,
    auctionsLoading,
    auctions
}) => {
    // Re-run when auctioneerId changes (navigation between auctioneers)
    useEffect(() => {
        if (onInitialize) {
            onInitialize();
        }
    }, [auctioneerId, onInitialize]);

    const name = auctioneer && getAuctioneerName(auctioneer);
    const designations = auctioneer && getDesignations(auctioneer);
    const website = auctioneer && prefixIfNeeded(auctioneer.webAddress);
    const location = auctioneer && getAuctioneerLocation(auctioneer, true);
    const shouldShowExpertise = !!(auctioneer && auctioneer.expertise && auctioneer.expertise.length);

    // Render auctions section
    const renderAuctions = () => {
        if (auctionsLoading && (!auctions || !auctions.length)) {
            return (
                <div className="al-auctioneer-auctions-loading">
                    <ProgressIndicator text="Loading auctions..." />
                </div>
            );
        }

        if (auctions && auctions.length) {
            return (
                <div className="al-auctioneer-auctions-grid">
                    {auctions.map(auction => (
                        <AuctionPreview key={auction.id} auction={auction} />
                    ))}
                </div>
            );
        }

        return (
            <div className="al-auctioneer-auctions-empty">
                <p>No auctions available</p>
            </div>
        );
    };

    // Header-only mode (embedded widget)
    if (hideHeader) {
        return (
            <div className="al-auctioneer-page">
                <section className="al-auctioneer-auctions">
                    {renderAuctions()}
                </section>
            </div>
        );
    }

    // Show loading state while fetching auctioneer data
    if (auctioneerLoading && !auctioneer) {
        return (
            <div className="al-auctioneer-page">
                <div className="al-auctioneer-auctions-loading">
                    <ProgressIndicator text="Loading auctioneer..." />
                </div>
            </div>
        );
    }

    return (
        <div className="al-auctioneer-page">
            {/* Profile Card */}
            <article className="al-auctioneer-profile">
                {/* Header with Photo */}
                <header className="al-auctioneer-profile-header">
                    {auctioneer && auctioneer.photoUrl && (
                        <AuctioneerPhoto
                            auctioneer={auctioneer}
                            className="al-auctioneer-profile-photo"
                        />
                    )}
                    <div className="al-auctioneer-profile-info">
                        <h1 className="al-auctioneer-profile-name">
                            {name}
                            {designations && (
                                <span className="al-auctioneer-profile-designations">
                                    {designations}
                                </span>
                            )}
                        </h1>
                        {auctioneer && auctioneer.title && (
                            <p className="al-auctioneer-profile-title">{auctioneer.title}</p>
                        )}
                        {auctioneer && auctioneer.company && (
                            <p className="al-auctioneer-profile-company">{auctioneer.company}</p>
                        )}
                    </div>
                </header>

                {/* Contact & Details */}
                <div className="al-auctioneer-profile-body">
                    {/* Contact Info */}
                    <div className="al-auctioneer-contact">
                        {auctioneer && auctioneer.phone && (
                            <a
                                href={`tel:${auctioneer.phone}`}
                                className="al-auctioneer-contact-item"
                            >
                                <Phone size={16} />
                                <span>{auctioneer.phone}</span>
                            </a>
                        )}

                        {auctioneer && auctioneer.address && (
                            <div className="al-auctioneer-contact-item">
                                <MapPin size={16} />
                                <span>{auctioneer.address}</span>
                            </div>
                        )}

                        {location && !auctioneer.address && (
                            <div className="al-auctioneer-contact-item">
                                <MapPin size={16} />
                                <span>{location}</span>
                            </div>
                        )}
                    </div>

                    {/* Bio */}
                    {auctioneer && auctioneer.bio && (
                        <div className="al-auctioneer-bio">
                            <p>{trim(auctioneer.bio)}</p>
                        </div>
                    )}

                    {/* Expertise */}
                    {shouldShowExpertise && (
                        <div className="al-auctioneer-expertise-section">
                            <h3 className="al-auctioneer-section-label">Expertise</h3>
                            <AuctioneerExpertise auctioneer={auctioneer} />
                        </div>
                    )}

                    {/* Actions - inline with content */}
                    {auctioneer && (auctioneer.email || website) && (
                        <div className="al-auctioneer-actions">
                            {auctioneer.email && (
                                <a
                                    className="al-auctioneer-action-btn al-auctioneer-action-btn--primary"
                                    href={`mailto:${auctioneer.email}`}
                                >
                                    <Mail size={16} />
                                    <span>Email</span>
                                </a>
                            )}
                            {website && (
                                <a
                                    className="al-auctioneer-action-btn"
                                    href={website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Globe size={16} />
                                    <span>Website</span>
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </article>

            {/* Auctions Section */}
            <section className="al-auctioneer-auctions">
                <h2 className="al-auctioneer-auctions-title">Auctions by {name}</h2>
                {renderAuctions()}
            </section>
        </div>
    );
};

AuctioneerPage.propTypes = {
    onInitialize: PropTypes.func,
    auctioneerId: PropTypes.string,
    auctioneer: AUCTIONEER_PROP_TYPE,
    auctioneerLoading: PropTypes.bool,
    hideHeader: PropTypes.bool,
    auctionsLoading: PropTypes.bool,
    auctions: PropTypes.arrayOf(AUCTION_PROP_TYPE)
};

export default AuctioneerPage;
